import { Component, h, Prop, Fragment, Watch, Event, EventEmitter, Method } from '@stencil/core';
import { __, sprintf } from '@wordpress/i18n';
import { speak } from '@wordpress/a11y';
import { zones } from '../../../functions/tax';

@Component({
  tag: 'sc-tax-id-input',
  styleUrl: 'sc-tax-id-input.css',
  shadow: true,
})
export class ScTaxIdInput {
  /** The input */
  private input: HTMLScInputElement;

  /** The country code. */
  @Prop() country: string;

  /** Force show the field. */
  @Prop() show: boolean = false;

  /** Type of tax id */
  @Prop({ mutable: true }) type: string = 'other';

  /** Tax ID Number */
  @Prop() number: string = null;

  /** The status */
  @Prop() status: 'valid' | 'invalid' | 'unknown' = 'unknown';

  /** Is this loading? */
  @Prop() loading: boolean;

  /** Help text. */
  @Prop() help: string;

  /** Other zones label */
  @Prop() otherLabel: string = __('Tax ID', 'surecart');

  /** GST zone label */
  @Prop() caGstLabel: string = __('GST Number', 'surecart');

  /** AU zone label */
  @Prop() auAbnLabel: string = __('ABN Number', 'surecart');

  /** UK zone label */
  @Prop() gbVatLabel: string = __('UK VAT', 'surecart');

  /** EU zone label */
  @Prop() euVatLabel: string = __('EU VAT', 'surecart');

  /** Tax ID Types which will be shown */
  @Prop() taxIdTypes: string[] = [];

  /** Whether tax input is required */
  @Prop({ reflect: true }) required: boolean = false;

  /** Make a request to update the order. */
  @Event() scChange: EventEmitter<{ number: string; number_type: string }>;

  /** Make a request to update the order. */
  @Event() scInput: EventEmitter<Partial<{ number: string; number_type: string }>>;

  /** Change the Type */
  @Event() scInputType: EventEmitter<string>;

  /** Set the checkout state. */
  @Event() scSetState: EventEmitter<string>;

  @Method()
  async reportValidity() {
    return this.input.reportValidity();
  }

  @Watch('otherLabel')
  @Watch('caGstLabel')
  @Watch('auAbnLabel')
  @Watch('gbVatLabel')
  @Watch('euVatLabel')
  onLabelChange() {
    zones.ca_gst.label = this.caGstLabel || zones.ca_gst.label;
    zones.au_abn.label = this.auAbnLabel || zones.au_abn.label;
    zones.gb_vat.label = this.gbVatLabel || zones.gb_vat.label;
    zones.eu_vat.label = this.euVatLabel || zones.eu_vat.label;
    zones.other.label = this.otherLabel || zones.other.label;
  }

  componentWillLoad() {
    this.onLabelChange();
  }

  renderStatus() {
    if (this.status === 'valid') {
      return <sc-icon name="check" slot="prefix" style={{ color: 'var(--sc-color-success-500)' }}></sc-icon>;
    }
    if (this.status === 'invalid') {
      return <sc-icon name="x" slot="prefix" style={{ color: 'var(--sc-color-danger-500)' }}></sc-icon>;
    }
  }

  filteredZones() {
    if (!!this.taxIdTypes.length) {
      return Object.keys(zones)
        .filter(name => this.taxIdTypes.includes(name))
        .reduce((obj, key) => {
          obj[key] = zones[key];
          return obj;
        }, {});
    }

    return zones;
  }

  @Watch('taxIdTypes')
  onTaxIdTypesChange() {
    // If there is no other type, set the first one as default type.
    if (!!this.taxIdTypes.length) {
      this.type = !this.taxIdTypes.includes('other') ? this.taxIdTypes[0] : 'other';
    }
  }

  getZoneLabel() {
    const filteredZones = this.filteredZones() || {};

    // Get the label of the current type or the other type.
    // If there is no other type, get the first one.
    return filteredZones?.[this?.type || 'other']?.label || filteredZones?.[Object.keys(filteredZones)[0]]?.label;
  }

  render() {
    return (
      <Fragment>
        <sc-input name="tax_identifier.number_type" required={this.required} value={this.type} style={{ display: 'none' }} />
        <sc-input
          ref={el => (this.input = el as HTMLScInputElement)}
          label={this.getZoneLabel()}
          aria-label={__('Tax ID', 'surecart')}
          placeholder={__('Enter Tax ID', 'surecart')}
          name="tax_identifier.number"
          value={this.number}
          onScInput={(e: any) => {
            e.stopImmediatePropagation();
            this.scInput.emit({
              number: e.target.value,
              number_type: this.type || 'other',
            });
          }}
          onScChange={(e: any) => {
            e.stopImmediatePropagation();
            this.scChange.emit({
              number: e.target.value,
              number_type: this.type || 'other',
            });
          }}
          help={this.help}
          required={this.required}
        >
          {this.loading && this.type === 'eu_vat' ? <sc-spinner slot="prefix" style={{ '--spinner-size': '10px' }}></sc-spinner> : this.renderStatus()}

          {Object.keys(this.filteredZones() || {})?.length === 1 ? (
            <span slot="suffix">{Object.values(this.filteredZones() || {})?.[0]?.['label_small']}</span>
          ) : (
            <sc-dropdown slot="suffix" position="bottom-right" role="select" aria-multiselectable="false" aria-label={__('Select number type', 'surecart')}>
              <sc-button type="text" slot="trigger" caret loading={false} style={{ color: 'var(--sc-input-label-color)' }} tabindex="0">
                {this.filteredZones()?.[this?.type || 'other']?.label_small}
              </sc-button>
              <sc-menu>
                {Object.keys(this.filteredZones() || {}).map(name => (
                  <sc-menu-item
                    role="option"
                    onClick={() => {
                      this.scInput.emit({
                        number: this.number,
                        number_type: name,
                      });
                      this.scChange.emit({
                        number: this.number,
                        number_type: name,
                      });
                      this.type = name;
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        this.scInput.emit({
                          number: this.number,
                          number_type: name,
                        });
                        this.scChange.emit({
                          number: this.number,
                          number_type: name,
                        });
                        this.type = name;
                        this.input?.triggerFocus();
                        speak(sprintf(__('%s selected', 'surecart'), zones[name].label_small, 'assertive'));
                      }
                    }}
                    checked={this.type === name}
                    aria-selected={this.type === name ? 'true' : 'false'}
                    aria-label={zones[name].label_small}
                  >
                    {zones[name].label_small}
                  </sc-menu-item>
                ))}
              </sc-menu>
            </sc-dropdown>
          )}
        </sc-input>
      </Fragment>
    );
  }
}
